import ReportPdf from './ReportPdf';
import { SortLines } from './Functions/LineSorter';
import { WriteLinesAndSetPosition } from './Functions/LineWriter';
import { NewSectionHeight } from './Functions/FieldHeights';
import {
    MultipleReportTables,
    SingleReportTable,
} from './Functions/MultipleReportTables';
import './Phetsarath OT-normal';
import {
    PlaceImages,
    PlaceSectionColumnFieldImages,
} from './Functions/ReportImagePlacer';
import {
    DoesDoubleFieldContainData,
    DoubleFieldColumn,
    DoubleFieldColumnWithRowHeading,
    SingleFieldColumn,
    SingleFieldColumnWithRowHeading,
    SingleFieldColumnWithDynamicHeader,
    SingleFieldColumnWithSeparateHeading,
} from './Functions/FieldColumn';
import { SectionHeading } from './Functions/Headings';
import { WriteAbsoluteSection } from './Functions/AbsoluteSectionWriter';

const writeReport = (data, config, reportName, language) => {
    const reportPdf = new ReportPdf(
        { left: 20, right: 20, top: 20, bottom: 12 },
        'a4'
    );

    // Write header and footer to first page
    if (hasLinesOrImages(config.Header)) {
        writeHeader(config.Header, reportPdf, data, language);
    }

        writeFooter(config.Footer, reportPdf, data, language);
    

    writeContent(config, reportPdf, data, language);

    // Write header and footer on the remaining pages after all contents have been written
    for (let i = 2; i <= reportPdf.getNumberOfPages(); i++) {
        reportPdf.setPage(i);
        if (hasLinesOrImages(config.Header)) {
            writeHeader(config.Header, reportPdf, data, language);
        }

        writeFooter(config.Footer, reportPdf, data, language);
        
    }

    if (typeof reportPdf.putTotalPages === 'function') {
        reportPdf.putTotalPages('{total_pages_count_string}');
    }
    return reportPdf;
};

const writeAndDownloadReport = (data, config, reportName, language) => {
    const reportPdf = writeReport(data, config, reportName, language);
    reportPdf.save(reportName);
};

const writeMultipleReports = (data, config, language) => {
    const reportPdf = new ReportPdf(
        { left: 20, right: 20, top: 20, bottom: 12 },
        'a4'
    );

    let totalPages = 0;
    const lastIndex = data.length - 1; // Get the index of the last item

    for (const [index, row] of data.entries()) {
        writeHeader(config.Header, reportPdf, row, language);
        writeFooter(config.Footer, reportPdf, row, language);

        writeContent(config, reportPdf, row, language);

        for (let i = totalPages; i < reportPdf.getNumberOfPages(); i++) {
            reportPdf.setPage(i + 1);
            writeHeader(config.Header, reportPdf, row, language);
            writeFooter(config.Footer, reportPdf, row, language);
        }
        // Only add a page if this is not the last item
        if (index < lastIndex) {
            reportPdf.addPage();
            totalPages = reportPdf.getNumberOfPages();
            reportPdf.setPage(totalPages);
        }
    }

    if (typeof reportPdf.putTotalPages === 'function') {
        reportPdf.putTotalPages('{total_pages_count_string}');
    }
    reportPdf.save('report.pdf'); // TODO: make this configurable
};

const writeHeader = (headerConfig, reportPdf, data, language) => {
    PlaceImages(headerConfig.Images, reportPdf);
    const lines = SortLines(headerConfig.Lines);
    reportPdf.setCurrentLine(getFirstLineNumber(lines));
    reportPdf.setCurrentLinePosition(
        reportPdf.getMargins(reportPdf.getCurrentPage).top
    );
    WriteLinesAndSetPosition(
        lines,
        reportPdf,
        data,
        language,
        false,
        headerConfig.LineSpacing ?? 4
    );
    reportPdf.horizontalRule();
    reportPdf.setHeaderHeight(reportPdf.getCurrentLinePosition());
};

const writeFooter = (footerConfig, reportPdf, data, language) => {
    PlaceImages(footerConfig.Images, reportPdf);
    const lines = SortLines(footerConfig.Lines, true);
    reportPdf.setCurrentLine(getLastLineNumber(lines));
    reportPdf.setCurrentLinePosition(
        reportPdf.getPageHeight() -
            reportPdf.getMargins(reportPdf.getCurrentPage).bottom
    );
    WriteLinesAndSetPosition(
        lines,
        reportPdf,
        data,
        language,
        true,
        footerConfig.LineSpacing ?? 4
    );
    reportPdf.horizontalRule();
    reportPdf.setFooterHeight(
        reportPdf.getPageHeight() - reportPdf.getCurrentLinePosition()
    );
};

const getFirstLineNumber = (lines) =>
    Math.min(...lines.map((item) => item.Line));
const getLastLineNumber = (lines) =>
    Math.max(...lines.map((item) => item.Line));

const writeContent = (lines, reportPdf, data, language) => {
    const headerHeight = reportPdf.getHeaderHeight();
    const footerHeight = reportPdf.getFooterHeight();
    let currentGroup = '';
    reportPdf.setCurrentLinePosition(headerHeight + 10);

    for (const section of lines.Contents) {
        if (currentGroup !== section.Group && section.Multiple) {
            currentGroup = section.Group;
            if (currentGroup && data.Groups) {
                const sectionData = data.Groups.find(
                    (f) =>
                        f.Key.toLowerCase() ===
                        section.MultipleName.toLowerCase()
                );
                const groupSections = lines.Contents.filter(
                    (f) =>
                        f.Group &&
                        f.Group.toLowerCase() === currentGroup.toLowerCase()
                );
                for (const groupData of sectionData.Multiple) {
                    for (const groupSection of groupSections) {
                        generateSection(
                            groupData,
                            groupSection,
                            footerHeight,
                            reportPdf,
                            lines,
                            true,
                            language
                        );
                    }
                    reportPdf.incrementCurrentLinePositionBy(15);
                }
            }
        } else {
            generateSection(
                data,
                section,
                footerHeight,
                reportPdf,
                lines,
                true,
                language
            );
        }
    }
};

const generateSection = (
    data,
    section,
    footerHeight,
    reportPdf,
    config,
    showHeading = true,
    language
) => {
    const sectionName = section.Name.toLowerCase();
    const sectionType = section.Type.toLowerCase();

    //TODO: We need to check if there are images and if there are check if they span over the page then factor that into the newsectioncalc

    const sectionHeight = NewSectionHeight(section, data, config.Contents);
    reportPdf.setCurrentLine(0);

    const footerTopPadding = reportPdf.spacing.md;
    const newSectionEndYPx =
        sectionHeight.singleHeight + reportPdf.getCurrentLinePosition();
    const footerYStartPx =
        reportPdf.getPageHeight() - (footerHeight + footerTopPadding);

    const isNewSectionOffPage = newSectionEndYPx > footerYStartPx;
    if (isNewSectionOffPage && sectionType !== 'table') {
        reportPdf.addPage();
        reportPdf.setCurrentLinePosition(reportPdf.getHeaderHeight() + 10);
    }

    if (!reportPdf.getSavedSectionHeading()) {
        reportPdf.setSavedSectionHeading(
            section.LinkedSections > 0 ? section.Heading : undefined
        );
    }

    if (isImagesInSection(section)) {
        const newImages = section.Images.map((image) => ({
            ...image,
            X: image.X ? image.X : reportPdf.getMargins().left,
            Y: image.Y
                ? image.Y + reportPdf.getCurrentLinePosition()
                : reportPdf.getCurrentLinePosition(),
        }));
        PlaceImages(newImages, reportPdf);
    }

    switch (sectionType) {
        case 'lines':
            WriteLinesAndSetPosition(
                section.Lines,
                reportPdf,
                data,
                language,
                false,
                section.LineSpacing ?? 2
            );
            break;
        case 'reportimage':
            PlaceSectionColumnFieldImages(section, reportPdf);
            break;
        case 'singlefieldcolumn':
            SingleFieldColumn(section, data, reportPdf);
            break;
        case 'singlefieldcolumnwithseparateheading':
            SingleFieldColumnWithSeparateHeading(section, data, reportPdf);
            break;
        case 'singlefieldcolumnwithrowheading':
            SingleFieldColumnWithRowHeading(section, data, reportPdf);
            break;
        case 'doublefieldcolumn':
            if (
                DoesDoubleFieldContainData(section, data) &&
                section.Heading &&
                showHeading
            ) {
                if(section.Heading && section.Heading[0].Text)
                SectionHeading(section.Heading, reportPdf);
                DoubleFieldColumn(section, data, reportPdf);
                reportPdf.setSavedSectionHeading(undefined);
            }
            break;
        case 'doublefieldcolumnwithrowheading':
            DoubleFieldColumnWithRowHeading(section, data, reportPdf);
            break;
        case 'dynamicsinglecolumnone':
            SingleFieldColumnWithDynamicHeader(section, data, reportPdf);
            break;
        case 'table':
            if (data.Tables?.length > 0) {
                if (
                    sectionName === 'organismlisttable' ||
                    sectionName === 'alerts' ||
                    sectionName === 'commentstable' ||
                    sectionName === 'specimencommentstable'
                ) {
                    SingleReportTable(section, reportPdf, data);
                } else {
                    MultipleReportTables(section, reportPdf, data);
                    if (section.LinkedSections < 1) {
                        reportPdf.setSavedSectionHeading(undefined);
                    }
                }
            }
            break;
        default:
            break;
    }

    if (section.Separator) {
        reportPdf.horizontalRule();
        reportPdf.incrementCurrentLinePositionBy(10);
    }
};

const isImagesInSection = (section) =>
    Array.isArray(section.Images) && section.Images.length > 0;

const hasLinesOrImages = (headerFooterConfig) => {
    return headerFooterConfig.Images?.length > 0 || headerFooterConfig.Lines?.length > 0;
};

export {
    writeReport as WriteReport,
    writeAndDownloadReport as WriteAndDownloadReport,
    writeMultipleReports as WriteMultipleReports,
};
